// src/taskpane/components/App.tsx
import * as React from "react";
import {
  Button,
  makeStyles,
  tokens,
  Text,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  Translate24Regular,
  DocumentBulletList24Regular,
  SlideText24Regular,
} from "@fluentui/react-icons";
import Settings, { loadSettings } from "./Settings";
import LanguageSelector from "./LanguageSelector";
import TranslationPanel, { Phase } from "./TranslationPanel";
import ManualTranslate from "./ManualTranslate";
import ErrorBoundary from "./ErrorBoundary";
import {
  extractAllText,
  extractCurrentSlideText,
  extractSelectedShapeText,
  getDebugLogs,
} from "../services/textExtractor";
import { translateAllText } from "../services/translator";
import { replaceAllText } from "../services/textReplacer";
import {
  AddinSettings,
  ExtractionResult,
  TranslationResult,
} from "../services/types";

type TranslateMode = "all" | "currentSlide" | "selected";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    padding: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  modeButtons: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  modeButton: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    justifyContent: "flex-start",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: "1px solid",
    borderColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    transition: "all 0.15s ease",
    width: "100%",
  },
  modeButtonHover: {
    borderColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  modeButtonTitle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  modeButtonDesc: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
  },
  footer: {
    marginTop: "auto",
    paddingTop: tokens.spacingVerticalM,
  },
});

export default function App() {
  const styles = useStyles();
  const [settings, setSettings] = React.useState<AddinSettings>(loadSettings);
  const [phase, setPhase] = React.useState<Phase>("idle");
  const [mode, setMode] = React.useState<TranslateMode>("all");
  const [sourceLang, setSourceLang] = React.useState("auto");
  const [targetLang, setTargetLang] = React.useState(
    () => loadSettings().defaultTargetLanguage
  );
  const [extractionResult, setExtractionResult] =
    React.useState<ExtractionResult | null>(null);
  const [translationResult, setTranslationResult] =
    React.useState<TranslationResult | null>(null);
  const [progress, setProgress] = React.useState({
    completed: 0,
    total: 0,
    current: "",
  });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showDebug, setShowDebug] = React.useState(false);
  const [insertSuccess, setInsertSuccess] = React.useState(false);

  const isProcessing =
    phase === "extracting" ||
    phase === "translating" ||
    phase === "replacing";

  /** 检查 API Key 并设置提取阶段 */
  const checkApiKey = (): boolean => {
    if (!settings.apiKey.trim()) {
      setErrorMessage("请先在设置中配置 Azure Translator API Key");
      setPhase("error");
      return false;
    }
    setPhase("extracting");
    setErrorMessage(null);
    return true;
  };

  /** 全部翻译 */
  const handleExtractAll = async () => {
    if (!checkApiKey()) return;
    try {
      const result = await extractAllText();
      setMode("all");
      setExtractionResult(result);
      if (result.totalTextItems === 0) {
        setErrorMessage("未在演示文稿中找到任何可翻译的文本内容");
        setPhase("error");
        return;
      }
      setPhase("extracted");
    } catch (error) {
      setErrorMessage(
        `文本提取失败: ${error instanceof Error ? error.message : String(error)}`
      );
      setPhase("error");
    }
  };

  /** 当前页翻译 */
  const handleExtractCurrentSlide = async () => {
    if (!checkApiKey()) return;
    try {
      const result = await extractCurrentSlideText();
      setMode("currentSlide");
      setExtractionResult(result);
      if (result.totalTextItems === 0) {
        setErrorMessage("当前页未找到任何可翻译的文本内容");
        setPhase("error");
        return;
      }
      setPhase("extracted");
    } catch (error) {
      setErrorMessage(
        `文本提取失败: ${error instanceof Error ? error.message : String(error)}`
      );
      setPhase("error");
    }
  };

  /** 选中文本框翻译 */
  const handleExtractSelected = async () => {
    if (!checkApiKey()) return;
    try {
      const result = await extractSelectedShapeText();
      setMode("selected");
      setExtractionResult(result);
      if (result.totalTextItems === 0) {
        setErrorMessage(
          "选中的形状中未找到可翻译的文本内容。请先在幻灯片中点击选中一个文本框。"
        );
        setPhase("error");
        return;
      }
      setPhase("extracted");
    } catch (error) {
      setErrorMessage(
        `文本提取失败: ${error instanceof Error ? error.message : String(error)}`
      );
      setPhase("error");
    }
  };

  /** 翻译文本 */
  const handleTranslate = async () => {
    if (!extractionResult) return;

    setPhase("translating");
    setErrorMessage(null);

    try {
      const result = await translateAllText(
        extractionResult.items,
        targetLang,
        sourceLang,
        settings,
        (completed, total, current) => {
          setProgress({ completed, total, current });
        }
      );
      setTranslationResult(result);

      if (result.failCount === result.items.length) {
        const firstError = result.items.find((i) => i.error);
        const errorDetail = firstError?.error || "未知错误";
        setErrorMessage(`翻译失败: ${errorDetail}`);
        setPhase("error");
        return;
      }

      setPhase("translated");
    } catch (error) {
      setErrorMessage(
        `翻译失败: ${error instanceof Error ? error.message : String(error)}`
      );
      setPhase("error");
    }
  };

  /** 应用翻译到演示文稿 */
  const handleApply = async () => {
    if (!translationResult) return;

    setPhase("replacing");
    setErrorMessage(null);

    try {
      await replaceAllText(translationResult.items);
      setPhase("done");
    } catch (error) {
      setErrorMessage(
        `替换失败: ${error instanceof Error ? error.message : String(error)}`
      );
      setPhase("error");
    }
  };

  /** 重置所有状态 */
  const handleReset = () => {
    setPhase("idle");
    setExtractionResult(null);
    setTranslationResult(null);
    setProgress({ completed: 0, total: 0, current: "" });
    setErrorMessage(null);
  };

  /** 手动翻译插入到选中文本框 */
  const handleManualInsert = async (translatedText: string) => {
    try {
      await PowerPoint.run(async (context) => {
        const selected = context.presentation.getSelectedShapes();
        selected.load("items");
        await context.sync();

        if (selected.items.length === 0) {
          throw new Error("请先在幻灯片中选中一个文本框");
        }

        const shape = selected.items[0];
        const textFrame = shape.getTextFrameOrNullObject();
        textFrame.load("isNullObject, hasText");
        await context.sync();

        if (textFrame.isNullObject) {
          throw new Error("选中的形状不支持文本编辑");
        }

        textFrame.textRange.text = translatedText;
        await context.sync();
      });

      setInsertSuccess(true);
      setTimeout(() => setInsertSuccess(false), 2000);
    } catch (error) {
      setErrorMessage(
        `插入失败: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  const handleSettingsSave = (newSettings: AddinSettings) => {
    setSettings(newSettings);
    if (newSettings.defaultTargetLanguage) {
      setTargetLang(newSettings.defaultTargetLanguage);
    }
  };

  const modeLabel =
    mode === "all"
      ? "全部幻灯片"
      : mode === "currentSlide"
      ? "当前页"
      : "选中文本框";

  return (
    <ErrorBoundary>
      <div className={styles.root}>
        {/* 头部 */}
        <div className={styles.header}>
          <div className={styles.title}>
            <Translate24Regular fontSize={24} />
            <Text size={400} weight="bold">
              PPT 翻译助手
            </Text>
          </div>
          <Settings settings={settings} onSave={handleSettingsSave} />
        </div>

        <Divider />

        {/* 语言选择 */}
        <div className={styles.section}>
          <LanguageSelector
            sourceLang={sourceLang}
            targetLang={targetLang}
            onSourceChange={setSourceLang}
            onTargetChange={setTargetLang}
            disabled={isProcessing}
          />
        </div>

        {/* 手动翻译区域 */}
        <ManualTranslate
          sourceLang={sourceLang}
          targetLang={targetLang}
          settings={settings}
          onInsert={handleManualInsert}
        />

        {insertSuccess && (
          <MessageBar intent="success">
            <MessageBarBody>已成功插入翻译结果到选中的文本框</MessageBarBody>
          </MessageBar>
        )}

        {/* 三种翻译模式按钮 */}
        {phase === "idle" && (
          <div className={styles.modeButtons}>
            <button
              className={styles.modeButton}
              onClick={handleExtractAll}
              disabled={isProcessing}
            >
              <DocumentBulletList24Regular fontSize={24} />
              <div>
                <div className={styles.modeButtonTitle}>
                  <Text weight="semibold" size={200}>
                    全部翻译
                  </Text>
                </div>
                <div className={styles.modeButtonDesc}>
                  扫描并翻译演示文稿中所有幻灯片的文本内容
                </div>
              </div>
            </button>

            <button
              className={styles.modeButton}
              onClick={handleExtractCurrentSlide}
              disabled={isProcessing}
            >
              <SlideText24Regular fontSize={24} />
              <div>
                <div className={styles.modeButtonTitle}>
                  <Text weight="semibold" size={200}>
                    当前页翻译
                  </Text>
                </div>
                <div className={styles.modeButtonDesc}>
                  仅翻译当前打开的幻灯片中的所有文本
                </div>
              </div>
            </button>

            <button
              className={styles.modeButton}
              onClick={handleExtractSelected}
              disabled={isProcessing}
            >
              <span style={{ fontSize: "24px" }}>🔤</span>
              <div>
                <div className={styles.modeButtonTitle}>
                  <Text weight="semibold" size={200}>
                    选中文本框翻译
                  </Text>
                </div>
                <div className={styles.modeButtonDesc}>
                  先在幻灯片中点击选中文本框，再点击此按钮翻译
                </div>
              </div>
            </button>
          </div>
        )}

        {/* 加载指示器 */}
        {isProcessing && (
          <Spinner
            label={
              phase === "extracting"
                ? "正在扫描..."
                : phase === "translating"
                ? "正在翻译..."
                : "正在替换文本..."
            }
            size="large"
          />
        )}

        {/* 翻译面板 */}
        <TranslationPanel
          phase={phase}
          extractionResult={extractionResult}
          translationResult={translationResult}
          progress={progress}
          errorMessage={errorMessage}
          modeLabel={modeLabel}
          onStartTranslation={handleTranslate}
          onApplyTranslation={handleApply}
          onReset={handleReset}
        />

        {/* 底部提示 */}
        <div className={styles.footer}>
          <Text
            size={100}
            style={{ color: tokens.colorNeutralForeground3 }}
          >
            支持 PowerPoint 2016+（桌面版与网页版）| 翻译由 Azure AI
            Translator 提供
          </Text>
          {extractionResult && (
            <Button
              appearance="subtle"
              size="small"
              onClick={() => setShowDebug(!showDebug)}
              style={{ marginTop: "8px" }}
            >
              {showDebug ? "隐藏扫描日志" : "查看扫描日志"}
            </Button>
          )}
          {showDebug && extractionResult && (
            <>
              <Button
                appearance="subtle"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(getDebugLogs().join("\n"));
                }}
                style={{ marginTop: "4px" }}
              >
                复制所有日志
              </Button>
              <pre
                style={{
                  fontSize: "11px",
                  backgroundColor: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  maxHeight: "300px",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  marginTop: "4px",
                }}
              >
                {getDebugLogs().join("\n")}
              </pre>
            </>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}
